import { useState, useEffect, useCallback } from 'react'
import { adminGetPredictions, adminDeletePrediction, adminDeleteAllPredictions, adminDeleteResults, adminResendConfirmation, adminNotifyParticipants, adminTestResultEmail, adminSetResults, getResults } from '../lib/api'
import type { Prediction, BabyResult } from '../types'

export default function AdminPage() {
  const [password, setPassword] = useState(() => sessionStorage.getItem('admin_pwd') ?? '')
  const [authed, setAuthed] = useState(false)
  const [pwdInput, setPwdInput] = useState('')
  const [pwdError, setPwdError] = useState('')

  const [predictions, setPredictions] = useState<Prediction[]>([])
  const [results, setResults] = useState<BabyResult | null>(null)
  const [loading, setLoading] = useState(false)

  const [resultForm, setResultForm] = useState({ baby_name: '', baby_weight: '', arrival_date: '', arrival_time: '' })
  const [saving, setSaving] = useState(false)
  const [saveMsg, setSaveMsg] = useState('')

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [notifying, setNotifying] = useState(false)
  const [notifyMsg, setNotifyMsg] = useState('')
  const [testingEmail, setTestingEmail] = useState(false)
  const [testEmailMsg, setTestEmailMsg] = useState('')
  const [rowAction, setRowAction] = useState<Record<string, 'sending' | 'done' | 'error'>>({})

  const load = useCallback(async (pwd: string) => {
    setLoading(true)
    try {
      const [preds, res] = await Promise.all([
        adminGetPredictions(pwd),
        getResults(),
      ])
      setPredictions(preds)
      setResults(res)
      setSelectedIds(new Set(preds.map((p) => p.id)))
      if (res) {
        const [datePart, timePart] = res.arrival_date.split('T')
        setResultForm({
          baby_name: res.baby_name,
          baby_weight: (res.baby_weight / 1000).toFixed(3),
          arrival_date: datePart,
          arrival_time: timePart ?? '',
        })
      }
    } catch {
      setAuthed(false)
      sessionStorage.removeItem('admin_pwd')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (password) {
      adminGetPredictions(password)
        .then((preds) => {
          setPredictions(preds)
          setAuthed(true)
          load(password)
        })
        .catch(() => setAuthed(false))
    }
  }, [])

  async function login() {
    setPwdError('')
    try {
      await adminGetPredictions(pwdInput)
      sessionStorage.setItem('admin_pwd', pwdInput)
      setPassword(pwdInput)
      setAuthed(true)
      load(pwdInput)
    } catch {
      setPwdError('Mot de passe incorrect')
    }
  }

  async function deletePrediction(id: string) {
    if (!confirm('Supprimer ce pronostic ?')) return
    await adminDeletePrediction(id, password)
    setPredictions((p) => p.filter((x) => x.id !== id))
  }

  async function saveResults() {
    setSaving(true)
    setSaveMsg('')
    try {
      const w = parseFloat(resultForm.baby_weight.replace(',', '.'))
      if (isNaN(w)) throw new Error('Poids invalide')
      const arrival_date = resultForm.arrival_time
        ? `${resultForm.arrival_date}T${resultForm.arrival_time}`
        : resultForm.arrival_date
      await adminSetResults({
        baby_name: resultForm.baby_name,
        baby_weight: w,
        arrival_date,
      }, password)
      setSaveMsg('✅ Résultats enregistrés ! Le palmarès est maintenant public.')
      const res = await getResults()
      setResults(res)
    } catch (e: unknown) {
      setSaveMsg('❌ ' + (e instanceof Error ? e.message : 'Erreur'))
    } finally {
      setSaving(false)
    }
  }

  async function testResultEmailSend() {
    setTestingEmail(true)
    setTestEmailMsg('')
    try {
      const w = parseFloat(resultForm.baby_weight.replace(',', '.'))
      const arrival_date = resultForm.arrival_time
        ? `${resultForm.arrival_date}T${resultForm.arrival_time}`
        : resultForm.arrival_date
      const preview = resultForm.baby_name && !isNaN(w) && resultForm.arrival_date
        ? { baby_name: resultForm.baby_name, baby_weight: Math.round(w * 1000), arrival_date }
        : undefined
      await adminTestResultEmail(password, preview)
      setTestEmailMsg('✅ Email test envoyé à remi.caussignac@gmail.com')
    } catch (e: unknown) {
      setTestEmailMsg('❌ ' + (e instanceof Error ? e.message : 'Erreur'))
    } finally {
      setTestingEmail(false)
    }
  }

  async function sendNotifications() {
    if (selectedIds.size === 0) return
    setNotifying(true)
    setNotifyMsg('')
    try {
      const { sent, errors } = await adminNotifyParticipants([...selectedIds], password)
      setNotifyMsg(`✅ ${sent} email${sent > 1 ? 's' : ''} envoyé${sent > 1 ? 's' : ''}${errors > 0 ? ` (${errors} erreur${errors > 1 ? 's' : ''})` : ''}.`)
      await load(password)
    } catch (e: unknown) {
      setNotifyMsg('❌ ' + (e instanceof Error ? e.message : 'Erreur'))
    } finally {
      setNotifying(false)
    }
  }

  async function resetAllPredictions() {
    if (!confirm(`Supprimer les ${predictions.length} pronostic${predictions.length > 1 ? 's' : ''} ? Cette action est irréversible.`)) return
    await adminDeleteAllPredictions(password)
    setPredictions([])
    setSelectedIds(new Set())
  }

  async function resetResults() {
    if (!confirm('Effacer les résultats publiés ? Le palmarès redeviendra inaccessible.')) return
    await adminDeleteResults(password)
    setResults(null)
    setResultForm({ baby_name: '', baby_weight: '', arrival_date: '', arrival_time: '' })
    setSaveMsg('')
    setNotifyMsg('')
  }

  async function resendConfirmation(id: string) {
    setRowAction((s) => ({ ...s, [id + '_conf']: 'sending' }))
    try {
      await adminResendConfirmation(id, password)
      setRowAction((s) => ({ ...s, [id + '_conf']: 'done' }))
    } catch {
      setRowAction((s) => ({ ...s, [id + '_conf']: 'error' }))
    }
  }

  async function resendResults(id: string) {
    setRowAction((s) => ({ ...s, [id + '_res']: 'sending' }))
    try {
      await adminNotifyParticipants([id], password)
      setRowAction((s) => ({ ...s, [id + '_res']: 'done' }))
      await load(password)
    } catch {
      setRowAction((s) => ({ ...s, [id + '_res']: 'error' }))
    }
  }

  function toggleId(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const optedIn = predictions.filter((p) => p.opt_in_leaderboard === 1).length
  const formHasData = !!(resultForm.baby_name && resultForm.baby_weight && resultForm.arrival_date)
  // time is optional but recommended for tiebreaking

  if (!authed) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-baby-50 to-amber-50 flex items-center justify-center px-4">
        <div className="card max-w-sm w-full animate-slide-up">
          <div className="text-center mb-6">
            <div className="text-4xl mb-2">🔐</div>
            <h1 className="text-xl font-bold text-stone-800">Administration</h1>
          </div>
          <input
            type="password"
            className="input-field"
            placeholder="Mot de passe admin"
            value={pwdInput}
            onChange={(e) => setPwdInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && login()}
            autoFocus
          />
          {pwdError && <p className="mt-2 text-xs text-red-500">{pwdError}</p>}
          <button className="btn-primary mt-4" onClick={login}>
            Connexion
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-baby-50 to-amber-50 py-8 px-4">
      <div className="max-w-3xl mx-auto space-y-5">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-extrabold text-baby-700">Admin 🛠️</h1>
            <p className="text-stone-500 text-sm">Bienvenue dans l'espace d'administration</p>
          </div>
          <button
            className="text-xs text-stone-400 hover:text-stone-600 underline"
            onClick={() => { sessionStorage.removeItem('admin_pwd'); setAuthed(false) }}
          >
            Déconnexion
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3">
          <StatCard icon="👥" label="Participants" value={predictions.length} />
          <StatCard icon="🏆" label="Au palmarès" value={optedIn} />
          <StatCard icon="📊" label="Résultats" value={results ? '✅ Publiés' : '⏳ En attente'} isText />
        </div>

        {loading && <p className="text-center text-stone-400 py-8">Chargement…</p>}

        {!loading && (
          <>
            {/* Results form */}
            <div className="card space-y-5">
              <div>
                <h2 className="text-lg font-bold text-stone-800 mb-1">
                  {results ? '✏️ Modifier les résultats' : '🎉 Saisir les résultats du bébé'}
                </h2>
                <p className="text-stone-500 text-sm">
                  {results
                    ? 'Le palmarès est public. Modifiez et sauvegardez si besoin.'
                    : 'Une fois enregistrés, le palmarès deviendra public sur /palmares.'}
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-stone-700 mb-1.5">✨ Prénom du bébé</label>
                  <input
                    type="text"
                    className="input-field"
                    placeholder="Prénom réel"
                    value={resultForm.baby_name}
                    onChange={(e) => setResultForm((f) => ({ ...f, baby_name: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-stone-700 mb-1.5">⚖️ Poids (en kg)</label>
                  <div className="flex gap-2 items-center">
                    <input
                      type="number"
                      step="0.001"
                      min="1.5"
                      max="6"
                      className="input-field flex-1"
                      placeholder="3.250"
                      value={resultForm.baby_weight}
                      onChange={(e) => setResultForm((f) => ({ ...f, baby_weight: e.target.value }))}
                    />
                    <span className="font-semibold text-stone-600">kg</span>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-stone-700 mb-1.5">📅 Date et heure de naissance</label>
                  <div className="flex gap-2">
                    <input
                      type="date"
                      className="input-field flex-1"
                      value={resultForm.arrival_date}
                      onChange={(e) => setResultForm((f) => ({ ...f, arrival_date: e.target.value }))}
                    />
                    <input
                      type="time"
                      className="input-field w-32"
                      placeholder="18:17"
                      value={resultForm.arrival_time}
                      onChange={(e) => setResultForm((f) => ({ ...f, arrival_time: e.target.value }))}
                    />
                  </div>
                  <p className="text-xs text-stone-400 mt-1">L'heure est utilisée pour départager les ex-æquo</p>
                </div>
              </div>

              {saveMsg && (
                <div className={`rounded-xl px-4 py-3 text-sm font-medium ${saveMsg.startsWith('✅') ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                  {saveMsg}
                </div>
              )}

              <button
                className="btn-primary"
                onClick={saveResults}
                disabled={saving || !formHasData}
              >
                {saving ? 'Enregistrement…' : results ? 'Mettre à jour les résultats' : 'Publier les résultats 🎉'}
              </button>
            </div>

            {/* Test email — visible dès que le formulaire est rempli */}
            {formHasData && predictions.length > 0 && (
              <div className="card border-2 border-dashed border-amber-300 bg-amber-50">
                <h2 className="text-base font-bold text-stone-800 mb-1">🧪 Tester l'email résultat</h2>
                <p className="text-stone-500 text-sm mb-3">
                  Envoie l'aperçu de l'email (données du formulaire ci-dessus) à <strong>remi.caussignac@gmail.com</strong> avant de publier.
                </p>
                {testEmailMsg && (
                  <div className={`rounded-xl px-4 py-3 text-sm font-medium mb-3 ${testEmailMsg.startsWith('✅') ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                    {testEmailMsg}
                  </div>
                )}
                <button
                  className="btn-primary"
                  onClick={testResultEmailSend}
                  disabled={testingEmail}
                >
                  {testingEmail ? 'Envoi en cours…' : '🧪 Envoyer l\'email test'}
                </button>
              </div>
            )}

            {/* Notification section — only when results are published */}
            {results && predictions.length > 0 && (
              <div className="card space-y-4">
                <div>
                  <h2 className="text-lg font-bold text-stone-800 mb-1">📧 Notifier les participants</h2>
                  <p className="text-stone-500 text-sm">
                    Choisissez qui recevra un email avec les résultats et son score.
                  </p>
                </div>

                <div className="flex items-center gap-3">
                  <button
                    className="text-xs font-semibold text-baby-600 hover:text-baby-700 underline"
                    onClick={() => setSelectedIds(new Set(predictions.map((p) => p.id)))}
                  >
                    Tout sélectionner
                  </button>
                  <span className="text-stone-300">|</span>
                  <button
                    className="text-xs font-semibold text-stone-400 hover:text-stone-600 underline"
                    onClick={() => setSelectedIds(new Set())}
                  >
                    Tout désélectionner
                  </button>
                  <span className="ml-auto text-xs text-stone-400 font-medium">
                    {selectedIds.size}/{predictions.length} sélectionné{selectedIds.size > 1 ? 's' : ''}
                  </span>
                </div>

                <div className="border border-stone-100 rounded-xl overflow-hidden">
                  {predictions.map((p, i) => (
                    <label
                      key={p.id}
                      className={`flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-stone-50 transition-colors ${i > 0 ? 'border-t border-stone-100' : ''}`}
                    >
                      <input
                        type="checkbox"
                        className="w-4 h-4 rounded accent-baby-500 flex-shrink-0"
                        checked={selectedIds.has(p.id)}
                        onChange={() => toggleId(p.id)}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-stone-800 text-sm truncate">{p.participant_name}</p>
                        <p className="text-xs text-stone-400 truncate">{p.participant_email}</p>
                      </div>
                      {p.notified_at ? (
                        <span className="flex-shrink-0 text-xs bg-green-50 text-green-700 border border-green-200 px-2 py-0.5 rounded-full font-medium">
                          ✅ Notifié
                        </span>
                      ) : (
                        <span className="flex-shrink-0 text-xs bg-stone-50 text-stone-400 border border-stone-200 px-2 py-0.5 rounded-full">
                          En attente
                        </span>
                      )}
                    </label>
                  ))}
                </div>

                {notifyMsg && (
                  <div className={`rounded-xl px-4 py-3 text-sm font-medium ${notifyMsg.startsWith('✅') ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                    {notifyMsg}
                  </div>
                )}

                {selectedIds.size > 100 && (
                  <p className="text-xs text-red-500 font-medium">Maximum 100 destinataires — désélectionnez {selectedIds.size - 100} participant{selectedIds.size - 100 > 1 ? 's' : ''}.</p>
                )}
                <button
                  className="btn-primary"
                  onClick={sendNotifications}
                  disabled={notifying || selectedIds.size === 0 || selectedIds.size > 100}
                >
                  {notifying
                    ? 'Envoi en cours…'
                    : `Envoyer à ${selectedIds.size} participant${selectedIds.size > 1 ? 's' : ''} 📧`}
                </button>
              </div>
            )}

            {/* Predictions table */}
            <div className="card overflow-x-auto">
              <h2 className="text-lg font-bold text-stone-800 mb-4">
                Pronostics ({predictions.length})
              </h2>
              {predictions.length === 0 ? (
                <p className="text-center text-stone-400 py-8">Aucun pronostic pour l'instant.</p>
              ) : (
                <table className="w-full text-sm min-w-[600px]">
                  <thead>
                    <tr className="text-left text-stone-400 border-b border-stone-100">
                      <th className="pb-3 font-semibold">Participant</th>
                      <th className="pb-3 font-semibold">Prénom</th>
                      <th className="pb-3 font-semibold">Poids</th>
                      <th className="pb-3 font-semibold">Date</th>
                      <th className="pb-3 font-semibold">Palmarès</th>
                      <th className="pb-3 font-semibold">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {predictions.map((p) => (
                      <tr key={p.id} className="border-b border-stone-50 hover:bg-stone-50">
                        <td className="py-3 pr-3">
                          <div className="font-semibold text-stone-800">{p.participant_name}</div>
                          <div className="text-xs text-stone-400">{p.participant_email}</div>
                          {p.notified_at && (
                            <div className="text-xs text-green-600 mt-0.5">✅ Résultats envoyés</div>
                          )}
                        </td>
                        <td className="py-3 pr-3 font-medium">{p.baby_name_guess}</td>
                        <td className="py-3 pr-3">{(p.baby_weight_guess / 1000).toFixed(3)} kg</td>
                        <td className="py-3 pr-3 whitespace-nowrap">
                          {new Date(p.arrival_date_guess.substring(0, 10) + 'T12:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}
                          {p.arrival_date_guess.length > 10 && (
                            <span className="text-stone-400 ml-1">{p.arrival_date_guess.substring(11, 16)}</span>
                          )}
                        </td>
                        <td className="py-3 pr-3">
                          {p.opt_in_leaderboard === 1
                            ? <span className="bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full text-xs font-semibold">Oui</span>
                            : <span className="bg-stone-100 text-stone-500 px-2 py-0.5 rounded-full text-xs">Non</span>
                          }
                        </td>
                        <td className="py-3">
                          <div className="flex items-center gap-1.5">
                            <ActionBtn
                              title="Renvoyer email de confirmation"
                              state={rowAction[p.id + '_conf']}
                              idle="📧"
                              onClick={() => resendConfirmation(p.id)}
                            />
                            {results && (
                              <ActionBtn
                                title="Envoyer les résultats"
                                state={rowAction[p.id + '_res']}
                                idle="🏆"
                                onClick={() => resendResults(p.id)}
                              />
                            )}
                            <button
                              onClick={() => deletePrediction(p.id)}
                              title="Supprimer ce pronostic (libère l'email)"
                              className="w-7 h-7 flex items-center justify-center rounded-lg text-red-400 hover:bg-red-50 hover:text-red-600 transition-colors text-base"
                            >
                              🗑️
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Danger zone */}
            <div className="border border-red-200 rounded-2xl p-5 bg-red-50">
              <h3 className="text-sm font-bold text-red-700 mb-3">⚠️ Zone de danger</h3>
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  className="flex-1 py-2.5 px-4 rounded-xl font-semibold text-sm text-red-600 bg-white border border-red-200 hover:bg-red-100 transition-colors disabled:opacity-40"
                  onClick={resetAllPredictions}
                  disabled={predictions.length === 0}
                >
                  🗑️ Supprimer tous les pronostics ({predictions.length})
                </button>
                <button
                  className="flex-1 py-2.5 px-4 rounded-xl font-semibold text-sm text-red-600 bg-white border border-red-200 hover:bg-red-100 transition-colors disabled:opacity-40"
                  onClick={resetResults}
                  disabled={!results}
                >
                  🔄 Effacer les résultats publiés
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

function ActionBtn({ title, state, idle, onClick }: {
  title: string
  state: 'sending' | 'done' | 'error' | undefined
  idle: string
  onClick: () => void
}) {
  return (
    <button
      title={title}
      onClick={onClick}
      disabled={state === 'sending'}
      className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-stone-100 transition-colors text-base disabled:opacity-50"
    >
      {state === 'sending' ? '⏳' : state === 'done' ? '✅' : state === 'error' ? '❌' : idle}
    </button>
  )
}

function StatCard({ icon, label, value, isText }: { icon: string; label: string; value: number | string; isText?: boolean }) {
  return (
    <div className="card text-center py-4">
      <div className="text-2xl mb-1">{icon}</div>
      <div className={`font-extrabold ${isText ? 'text-sm' : 'text-2xl'} text-baby-600`}>{value}</div>
      <div className="text-xs text-stone-400 mt-0.5">{label}</div>
    </div>
  )
}
